// src/pages/Products.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  ArrowUpTrayIcon, 
  AdjustmentsHorizontalIcon, 
  DocumentArrowDownIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { productService } from '../services/products';
import DataTable from '../components/Common/DataTable';
import Modal from '../components/Common/Modal';
import ProductForm from '../components/Products/ProductForm';
import StockAdjustment from '../components/Products/StockAdjustment';
import BulkImport from '../components/Products/BulkImport';
import { toast } from 'react-hot-toast';

export default function Products() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    type: 'all', // all, products, services
    lowStock: false
  });
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['products', filters],
    queryFn: () => productService.getProducts(filters)
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => productService.getCategories()
  });

  const updateStockMutation = useMutation({
    mutationFn: ({ productId, data }) => productService.updateStock(productId, data),
    onSuccess: () => {
      toast.success('Stock updated successfully');
      queryClient.invalidateQueries(['products']);
      setShowStockModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update stock');
    }
  });

  // Helper function to calculate inclusive price for display only
  const getInclusivePrice = (exclusivePrice, taxRate = 16) => {
    return exclusivePrice * (1 + taxRate / 100);
  };

  // Download simplified import template
  const downloadTemplate = () => {
    setDownloadingTemplate(true);
    
    // Headers for both products and services
    const headers = [
      'type',              // 'product' or 'service'
      'sku',
      'name', 
      'description',
      'category',
      'price',             // Price without VAT
      'cost_price',        // Only for products
      'tax_rate',          // Default 16% if empty
      'unit_type',         // For services: piece, hour, day, month
      'current_stock',     // Only for products
      'min_stock_level',   // Only for products
      'barcode'
    ];
    
    // Sample product row
    const sampleProduct = {
      type: 'product',
      sku: 'PROD-001',
      name: 'Sample Product',
      description: 'Product description here',
      category: 'Electronics',
      price: '2500',
      cost_price: '2000',
      tax_rate: '16',
      unit_type: 'piece',
      current_stock: '100',
      min_stock_level: '10',
      barcode: '1234567890123'
    };
    
    // Sample service row
    const sampleService = {
      type: 'service',
      sku: 'SERV-001',
      name: 'Installation Service',
      description: 'Professional installation service',
      category: 'Services',
      price: '5000',
      cost_price: '',
      tax_rate: '16',
      unit_type: 'hour',
      current_stock: '',
      min_stock_level: '',
      barcode: ''
    };
    
    const csvRows = [];
    csvRows.push(headers.join(','));
    
    // Add sample product
    const productValues = headers.map(header => {
      const value = sampleProduct[header] || '';
      return `"${value}"`;
    });
    csvRows.push(productValues.join(','));
    
    // Add sample service
    const serviceValues = headers.map(header => {
      const value = sampleService[header] || '';
      return `"${value}"`;
    });
    csvRows.push(serviceValues.join(','));
    
    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', 'brominon_product_import_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setDownloadingTemplate(false);
    toast.success('Template downloaded successfully');
  };

  const columns = [
    { 
      key: 'product_type', 
      header: 'Type', 
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
          value === 'service' 
            ? 'bg-purple-100 text-purple-800' 
            : 'bg-blue-100 text-blue-800'
        }`}>
          {value === 'service' ? (
            <>
              <WrenchScrewdriverIcon className="h-3 w-3 mr-1" />
              Service
            </>
          ) : (
            <>
              <ShoppingBagIcon className="h-3 w-3 mr-1" />
              Product
            </>
          )}
        </span>
      )
    },
    { key: 'sku', header: 'SKU', sortable: true },
    { key: 'name', header: 'Name', sortable: true },
    { key: 'category', header: 'Category', sortable: true },
    { 
      key: 'unit_price', 
      header: 'Price (Excl. VAT)', 
      sortable: true, 
      format: 'currency',
      render: (value) => `KES ${value?.toLocaleString()}`
    },
    { 
      key: 'price_inclusive', 
      header: 'Price (Incl. VAT)', 
      sortable: true,
      render: (_, row) => {
        const inclusive = getInclusivePrice(row.unit_price, row.tax_rate || 16);
        return <span className="font-semibold text-green-600">KES {inclusive.toLocaleString()}</span>;
      }
    },
    { 
      key: 'current_stock', 
      header: 'Stock', 
      sortable: true,
      render: (value, row) => {
        if (row.product_type === 'service') {
          return <span className="text-gray-400">N/A</span>;
        }
        return (
          <span className={`font-semibold ${value <= row.min_stock_level ? 'text-red-600' : 'text-green-600'}`}>
            {value}
          </span>
        );
      }
    },
    { 
      key: 'tax_rate', 
      header: 'VAT %', 
      sortable: true,
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${value === 0 ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
          {value || 16}%
        </span>
      )
    }
  ];

  const handleAdjustStock = (product) => {
    if (product.product_type === 'service') {
      toast.error('Services do not have stock tracking');
      return;
    }
    setSelectedProduct(product);
    setShowStockModal(true);
  };

  const handleUpdateStock = (data) => {
    updateStockMutation.mutate({ productId: selectedProduct.id, data });
  };

  const actions = [
    {
      label: 'Edit',
      onClick: (row) => console.log('Edit', row),
      icon: 'edit'
    },
    {
      label: 'Adjust Stock',
      onClick: handleAdjustStock,
      icon: 'adjustments',
      show: (row) => row.product_type !== 'service'
    },
    {
      label: 'View History',
      onClick: (row) => console.log('History', row),
      icon: 'clock'
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products & Services</h1>
          <p className="text-gray-600">Manage your inventory products and services</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={downloadTemplate}
            disabled={downloadingTemplate}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            {downloadingTemplate ? 'Downloading...' : 'Download Template'}
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
            Bulk Import
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Item
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700">
              <strong>💡 Pricing Guide:</strong> Enter price without VAT. System adds {data?.taxRate || 16}% automatically.
              Example: Enter 2500 → Customer pays 2900 (including 400 VAT).
            </p>
            <p className="text-xs text-blue-600 mt-1">
              • <strong>Products</strong> track inventory and have cost prices
              • <strong>Services</strong> (labour, installation, washing) don't track inventory
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <input
            type="text"
            placeholder="Search by name, SKU..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Items</option>
            <option value="products">Products Only</option>
            <option value="services">Services Only</option>
          </select>
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories?.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.lowStock}
              onChange={(e) => setFilters({ ...filters, lowStock: e.target.checked })}
              className="rounded"
              disabled={filters.type === 'services'}
            />
            <span>Low Stock Only</span>
          </label>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        actions={actions}
        pagination={data?.pagination}
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Item"
        size="large"
      >
        <ProductForm onSuccess={() => {
          setShowCreateModal(false);
          queryClient.invalidateQueries(['products']);
        }} />
      </Modal>

      {/* Stock Adjustment Modal (Products only) */}
      <Modal
        isOpen={showStockModal}
        onClose={() => setShowStockModal(false)}
        title={`Adjust Stock - ${selectedProduct?.name}`}
      >
        <StockAdjustment
          product={selectedProduct}
          onConfirm={handleUpdateStock}
          onCancel={() => setShowStockModal(false)}
        />
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Bulk Import Items"
        size="large"
      >
        <BulkImport onSuccess={() => {
          setShowImportModal(false);
          queryClient.invalidateQueries(['products']);
        }} />
      </Modal>
    </div>
  );
}